
import { defineComponent, ref, Ref, watch, unref, onBeforeUnmount, onMounted, onUnmounted } from 'vue';
import HighchartsMore from 'highcharts/highcharts-more';
import * as Highcharts from 'highcharts';
import HighchartsBoost from 'highcharts/modules/boost';
import { chart, Chart, Options } from 'highcharts';
import isEqual from 'lodash-es/isEqual';

export default defineComponent({
    props: {
        options: {
            type: Object as () => Highcharts.Options,
            default: null,
        },
        chartModule: {
            type: Function,
            default: null,
        },
        printTitle: {
            type: String,
            default: '',
        },
    },
    setup(props) {
        const element: Ref<HTMLElement | null> = ref(null);

        if (props.chartModule) {
            HighchartsMore(Highcharts);
            props.chartModule(Highcharts);
        }

        HighchartsBoost(Highcharts);

        const highcharts = ref<Chart>();
        let currentOptions: any = null;

        onMounted(() => {
            highcharts.value = chart(element.value as HTMLElement, {});
        });

        onBeforeUnmount(() => {
            if (highcharts.value) {
                highcharts.value.destroy();
            }
        });

        function updateChart() {
            const newOptions = unref(props.options);
            if (highcharts.value && !isEqual(currentOptions, newOptions)) {
                currentOptions = newOptions;
                highcharts.value.update(unref(props.options), true, true, true);
            }
        }

        watch(() => highcharts, updateChart, { deep: true, immediate: true });
        watch(() => props.options, updateChart, { deep: true, immediate: false });

        let resetParams: [number | undefined, number | undefined, boolean, string | undefined] | null = null;

        const beforePrintEventListener = () => {
            resetParams = [highcharts.value?.chartWidth, highcharts.value?.chartHeight, false, highcharts.value?.options.title?.text];
            highcharts.value?.setSize(800, 550, false);
            if (props.printTitle) {
                highcharts.value?.setTitle({ text: props.printTitle });
            }
        };
        const afterPrintEventListener = () => {
            if (resetParams && highcharts.value) {
                // Only calling setSize with its previous width/height will set an absolute width without allowing resizing based on screen size
                // Only calling setSize with null null will make the print size persist after printing.
                // We need to call both to return to normal behaviour.
                highcharts.value?.setSize(resetParams[0], resetParams[1], resetParams[2]);
                highcharts.value?.setSize(null, null, resetParams[2]);
                if (props.printTitle) {
                    highcharts.value?.setTitle({ text: resetParams[3] });
                }
            }
        };

        window.addEventListener('beforeprint', beforePrintEventListener);
        window.addEventListener('afterprint', afterPrintEventListener);
        onUnmounted(() => {
            window.removeEventListener('beforeprint', beforePrintEventListener);
            window.removeEventListener('afterprint', afterPrintEventListener);
        });

        return { chart, element, options: props.options, highcharts, onMounted, onBeforeUnmount };
    },
});
